import { DealDetail, NegotiationFieldMeta } from 'api'
import { Locale, Translation } from 'locale'
import { getNegotiatedValue } from 'model'
import React, { useMemo } from 'react'
import { TableColumn, TableOptions } from 'view/components/panels'
import { useAuthorisedUser, useIsAdmin, useUserPreference } from 'view/hooks'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import { getDealModificationColour } from 'view/routes/deals/components/utils'
import { getStringModificationType } from 'view/routes/requests/shared/deltaGetters'
import { getNotionalFormatOptions } from 'view/shared/datagrid'
import { OrganisationDecisionMakerSelectboxField } from 'view/shared/entity'

export const useBulkCancelColumns = (
  onUpdate: (payload: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => void
) => {
  const userPreference = useUserPreference()
  const isAdmin = useIsAdmin()
  const user = useAuthorisedUser()

  const columns = useMemo<TableColumn<DealDetail>[]>(
    () => [
      {
        colId: 'tradeId',
        headerName: Locale.translate(Translation.TC_DEAL_ID),
        valueGetter: ({ data: { deal } }) => {
          return deal?.dealId
        },
        styles: { left: true }
      },
      {
        colId: 'collateral',
        headerName: Locale.translate(Translation.TC_COLLATERAL),
        valueGetter: ({ data }) => {
          return data.collateral?.name ?? data.collateralBasket?.name ?? ''
        },
        styles: { left: true }
      },
      {
        colId: 'notional',
        headerName: Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat }),
        valueGetter: ({ data: { deal } }) => {
          return Locale.formatNumber(deal.notional, getNotionalFormatOptions(userPreference.amountFormat, false))
        }
      },
      {
        colId: 'startDate',
        headerName: Locale.translate(Translation.TC_START_DATE),
        styles: { overflow: 'visible' },
        valueGetter: ({ data }) => Locale.formatDate(data.deal.startDate, userPreference.dateFormat)
      },
      {
        colId: 'endDate',
        headerName: Locale.translate(Translation.TC_END_DATE),
        styles: { overflow: 'visible' },
        valueGetter: ({ data }) => Locale.formatDate(data.deal.endDate, userPreference.dateFormat)
      },
      {
        colId: 'investmentDecisionMaker',
        headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER_ABBR),
        cellRenderer: ({ data: dealDetail }) => {
          const validationIssues = dealDetail.servicingSideDetails?.validationIssues ?? []
          const sideFields = dealDetail?.servicingSideDetails?.sideFields
          const organisationId = isAdmin ? dealDetail.deal.initiatorOrganisationId : user.organisationId
          const idmField = dealDetail.ownOrder
            ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
            : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER

          return (
            <OrganisationDecisionMakerSelectboxField
              organisationId={organisationId}
              label={''}
              fieldName={idmField}
              validationField={
                dealDetail.ownOrder
                  ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                  : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
              }
              validationIssues={validationIssues}
              underline={getDealModificationColour(
                () => getStringModificationType(sideFields?.[idmField]?.negotiableFieldRequired),
                dealDetail
              )}
              value={getNegotiatedValue(sideFields?.[idmField]?.negotiableFieldRequired)}
              onUpdate={(value, field) =>
                onUpdate({
                  value,
                  field,
                  dealId: dealDetail?.deal?.dealId
                })
              }
            />
          )
        }
      },
      {
        colId: 'executionDecisionMaker',
        headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER_ABBR),
        cellRenderer: ({ data: dealDetail }) => {
          const validationIssues = dealDetail.servicingSideDetails?.validationIssues ?? []
          const sideFields = dealDetail?.servicingSideDetails?.sideFields
          const organisationId = isAdmin ? dealDetail.deal.initiatorOrganisationId : user.organisationId
          const edmField = dealDetail.ownOrder
            ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
            : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER

          return (
            <OrganisationDecisionMakerSelectboxField
              label={''}
              fieldName={edmField}
              validationField={
                dealDetail.ownOrder
                  ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER
                  : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
              }
              validationIssues={validationIssues}
              underline={getDealModificationColour(
                () => getStringModificationType(sideFields?.[edmField]?.negotiableFieldRequired),
                dealDetail
              )}
              value={getNegotiatedValue(sideFields?.[edmField]?.negotiableFieldRequired)}
              onUpdate={(value, field) =>
                onUpdate({
                  value,
                  field,
                  dealId: dealDetail?.deal?.dealId
                })
              }
              organisationId={organisationId}
            />
          )
        }
      }
    ],
    [isAdmin, onUpdate, user.organisationId, userPreference.amountFormat, userPreference.dateFormat]
  )

  return columns
}

export const bulkCloseOpenOptions: TableOptions<DealDetail> = {
  getRowId: row => row.deal.dealId,
  autoExpandRows: true
}
